import {
  type FC, memo, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  DRAWING_TOOL_CIRCLE_MAX_RADIUS, DRAWING_TOOL_CIRCLE_MIN_RADIUS,
} from '~/drawingTool/drawingTool.constants';
import { DrawingTool } from '~/drawingTool/drawingTool.enums';
import { useDrawingInstanceZIndex } from '~/map/zIndexes/useDrawingInstanceZIndex.hook';
import { drawingEditSetSelectedDrawing } from '~/store/frontendState/mapTools/drawing/drawingEdit/drawingEdit.actionCreators';
import { type DrawingItemCircle } from '~/store/mapSettings/drawing/items/drawingItems.types';
import { useAreDrawingEventsEnabledRef } from '../hooks/useAreDrawingEventsEnabledRef';
import { useDrawingToolItemSizeForCurrentZoom } from '../hooks/useDrawingToolItemSizeForCurrentZoom';
import { useDrawingToolItemMouseEvents } from '../useDrawingToolItemMouseEvents';
import { DrawingToolCircleInstanceCircleContainer } from './drawingToolCircleInstanceCircle.container';

type MapCircleInstanceProps = {
  instance: DrawingItemCircle;
};

const DrawingToolCircleInstanceContainer: FC<MapCircleInstanceProps> = ({ instance }) => {
  const drawingEventsEnabledRef = useAreDrawingEventsEnabledRef();
  const { onMouseOut: onCircleMouseOut, onMouseOver: onCircleMouseOver, onRightClick } = useDrawingToolItemMouseEvents();
  const dispatch = useDispatch();
  const zIndex = useDrawingInstanceZIndex(instance.id, instance.placement);

  const radiusOnCurrentZoom = useDrawingToolItemSizeForCurrentZoom({
    itemSize: instance.settings.radius,
    min: 0,
    max: DRAWING_TOOL_CIRCLE_MAX_RADIUS,
    scalesWithMapZoom: instance.settings.scalesWithMapZoom,
  });

  const onCircleClick = useCallback((circleAreaId: string, e: MapObjectClickEventArgs) => {
    if (!drawingEventsEnabledRef.current) {
      return;
    }

    e.stopPropagation();

    dispatch(drawingEditSetSelectedDrawing(
      circleAreaId,
      {
        type: DrawingTool.Circle,
        value: {
          id: circleAreaId,
          placement: instance.placement,
          center: instance.center,
          settings: instance.settings,
        },
      })
    );
  }, [dispatch, instance.settings, instance.center, instance.placement, drawingEventsEnabledRef]);

  const onCircleRightClick = useCallback((_circleAreaId: Uuid, e: MapObjectClickEventArgs) => {
    if (!drawingEventsEnabledRef.current || !e.latLng) {
      return;
    }

    e.stopPropagation();
    onRightClick(e.latLng, {
      type: DrawingTool.Circle,
      value: instance,
    });
  }, [onRightClick, instance, drawingEventsEnabledRef]);

  if (radiusOnCurrentZoom < DRAWING_TOOL_CIRCLE_MIN_RADIUS) {
    return null;
  }

  return (
    <DrawingToolCircleInstanceCircleContainer
      instance={instance}
      zIndex={zIndex}
      onCircleClick={onCircleClick}
      onCircleMouseOut={onCircleMouseOut}
      onCircleMouseOver={onCircleMouseOver}
      onCircleRightClick={onCircleRightClick}
    />
  );
};

const pureComponent = memo(DrawingToolCircleInstanceContainer);
export { pureComponent as DrawingToolCircleInstanceContainer };
